import { ref, computed } from 'vue';

/**
 * RecentDocs (v3 homepage) — the "Recent jobs" list (prototype V3DocList): the
 * most recently created documents, from `POST Report/recentDocs`. Compact rows:
 * title + reference · relative date · lifecycle status dot-pill. `self` pattern;
 * caller injects an axios-like `http`. Navigation stays in the presentation.
 *
 * @param {Object} options
 * @param {{post:Function}} options.http
 * @param {() => number}   [options.getLimit] rows (default 8)
 * @param {() => string[]} [options.getDocTypes] doc-type filter (tmpl_name)
 * @param {() => string[]} [options.getStatuses] lifecycle-status filter (name)
 * @param {() => void}     [options.onReady] called once after the first fetch
 */
const SERVER_LIMIT = 50;

const relTime = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const day = 86400000;
  if (diff < 0) return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  if (diff < day) return 'Today';
  if (diff < 2 * day) return 'Yesterday';
  if (diff < 7 * day) return `${Math.floor(diff / day)} days ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export function recentDocs(options) {
  const { http, getLimit = () => 8, getDocTypes = () => [], getStatuses = () => [], onReady = () => {} } = options || {};
  const limit = () => getLimit() || 8;

  const self = { rows: ref([]), loading: ref(false) };

  self.isLoading = computed(() => self.loading.value);
  self.count = computed(() => self.rows.value.length);
  self.shownRows = computed(() => self.rows.value.slice(0, limit()));

  self.title = (r) => (r && (r.doc_title || r.doc_name || r.reference)) || '';
  self.reference = (r) => (r && r.reference) || '';
  self.docType = (r) => (r && r.doc_type) || '';
  self.whenText = (r) => relTime(r && r.created_date);
  self.statusLabel = (r) => (r && r.lifecycle_status_name) || '';
  // lifecycle_status.colour is a CSS colour/hex in the DB; fall back to neutral.
  self.statusColour = (r) => (r && r.lifecycle_status_colour) || 'var(--v3-text-3)';

  // Owner (creator) — for the fuller DocListFull table.
  self.owner = (r) => (r && r.owner_name ? String(r.owner_name).trim() : '');
  self.initials = (name) => {
    const parts = String(name || '').trim().split(/\s+/);
    if (!parts[0]) return '–';
    return ((parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  };

  // Inline quality METER (prototype V3ScoreMeter — the design replaced the
  // ring: "a ring can't be compared down a column, a bar aligns and scans").
  // Tone bands per the design: ≥80 green · ≥60 accent · else attn.
  self.hasScore = (r) => r != null && r.score != null && r.score !== '';
  self.scoreVal = (r) => Math.round(Number(r && r.score) || 0);
  self.scoreTone = (r) => {
    const s = Number(r && r.score) || 0;
    return s >= 80 ? 'green' : s >= 60 ? 'accent' : 'attn';
  };
  self.scorePct = (r) => Math.max(0, Math.min(100, Number(r && r.score) || 0));

  async function load() {
    self.loading.value = true;
    try {
      const resp = await http.post('Report/recentDocs', {
        limit: SERVER_LIMIT,
        docTypes: getDocTypes() || [],
        statuses: getStatuses() || [],
      });
      const data = resp && resp.data;
      self.rows.value = Array.isArray(data) ? data : data && Array.isArray(data.Data) ? data.Data : [];
    } catch {
      self.rows.value = [];
    } finally {
      self.loading.value = false;
    }
  }

  let didReady = false;
  Promise.resolve(load()).finally(() => {
    if (!didReady) {
      didReady = true;
      onReady();
    }
  });

  return self;
}
